import React, { useEffect, useState } from "react";
import { Menu, X, MessageCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { LOGOS, NAV_LINKS } from "../../lib/constants";

export const Navigation = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setOpen(false);
    // On non-home routes, navigate to home with the hash so Home can scroll to it.
    if (location.pathname !== "/") {
      navigate("/" + href);
      return;
    }
    // Defer scroll so the mobile menu collapse animation doesn't fight with scroll
    setTimeout(() => {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#1A1A2E]/85 backdrop-blur-xl border-b border-white/10"
          : "bg-[#1A1A2E]/40 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 sm:h-24 flex items-center justify-between">
        {/* Logo */}
        <button
          data-testid="nav-logo"
          onClick={() => handleNav("#home")}
          className="flex items-center group"
          aria-label="Ping home"
        >
          <div className="h-12 w-12 sm:h-14 sm:w-14 bg-white rounded-xl shadow-lg shadow-[#7B5EA7]/10 overflow-hidden flex items-center justify-center group-hover:scale-105 transition-transform">
            <img
              src={LOGOS.main}
              alt="Ping"
              className="w-[145%] h-[145%] object-contain"
            />
          </div>
        </button>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <button
              key={link.href}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => handleNav(link.href)}
              className="text-sm font-medium text-white/75 hover:text-white transition-colors relative group"
            >
              {link.label}
              <span className="absolute -bottom-1.5 left-0 w-0 h-[2px] ping-gradient group-hover:w-full transition-all duration-300" />
            </button>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden lg:flex items-center">
          <button
            data-testid="nav-cta"
            onClick={() => handleNav("#contact")}
            className="ping-gradient text-white font-semibold text-sm px-5 py-2.5 rounded-full hover:shadow-lg hover:shadow-[#7B5EA7]/30 hover:scale-[1.03] active:scale-[0.98] transition-all flex items-center gap-2"
          >
            <MessageCircle size={16} strokeWidth={2.5} />
            Get in Touch
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-white p-2 -mr-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="lg:hidden bg-[#1A1A2E] border-t border-white/10 overflow-hidden"
          >
            <div className="px-5 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.href}
                  data-testid={`nav-mobile-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => handleNav(link.href)}
                  className="text-left text-white/85 hover:text-white text-base font-medium py-3 px-2 rounded-lg hover:bg-white/5 transition-colors"
                >
                  {link.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-cta"
                onClick={() => handleNav("#contact")}
                className="ping-gradient text-white font-semibold text-sm px-5 py-3 rounded-full mt-3 flex items-center justify-center gap-2"
              >
                <MessageCircle size={16} strokeWidth={2.5} />
                Get in Touch
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navigation;
